import { useState, useRef, useCallback, useEffect } from 'react';
import { X, ChevronLeft, Zap, Camera, Sparkles, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getFunctionUrlWithApiKey } from '@/lib/edgeFunctionUrl';
import { useAuth } from '@/hooks/useAuth';
import { toast as sonnerToast } from 'sonner';
import { offlineMutation } from '@/lib/offlineStorage';
import { getTodayLocalString } from '@/lib/dateUtils';
import { ErrorWithTicket } from '@/components/support/ErrorWithTicket';

export type MealTime = 'desayuno' | 'comida' | 'cena' | 'colacion' | 'snack';

export const MEAL_OPTIONS: { value: MealTime; label: string; emoji: string }[] = [
  { value: 'desayuno', label: 'Desayuno', emoji: '🌅' },
  { value: 'comida',   label: 'Comida',   emoji: '🥗' },
  { value: 'cena',     label: 'Cena',     emoji: '🌙' },
  { value: 'colacion', label: 'Colación', emoji: '🍎' },
  { value: 'snack',    label: 'Snack',    emoji: '🍿' },
];

interface SavedFoodItem {
  item_name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meal_type: string;
  log_date: string;
}

/** Item devuelto por la edge function (antes de persistir) */
interface AnalyzedItem {
  item: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  activeUser: string;
  onSaved: (items?: SavedFoodItem[]) => void;
  initialMealTime?: MealTime;
}

type Step = 'meal' | 'input' | 'analyzing' | 'preview' | 'error';

export function FoodRegistrationFlow({ open, onClose, activeUser, onSaved, initialMealTime }: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>(initialMealTime ? 'input' : 'meal');
  const [mealTime, setMealTime] = useState<MealTime>(initialMealTime || 'comida');
  const [aiInput, setAiInput] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Estado del análisis
  const [analyzedItems, setAnalyzedItems] = useState<AnalyzedItem[]>([]);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string>('');
  // Guardamos lo último que el usuario intentó analizar — útil para
  // "Reintentar" o pre-llenar el ticket si pide soporte
  const lastAttemptRef = useRef<{ text?: string; imageBase64?: string; imageType?: string }>({});
  const progressTimerRef = useRef<number | null>(null);

  const reset = useCallback(() => {
    setStep(initialMealTime ? 'input' : 'meal');
    setMealTime(initialMealTime || 'comida');
    setAiInput('');
    setAnalyzedItems([]);
    setErrorMsg('');
    setAnalyzeProgress(0);
    if (progressTimerRef.current != null) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, [initialMealTime]);

  const handleClose = () => { reset(); onClose(); };

  // Limpieza al desmontar o cerrar
  useEffect(() => {
    return () => {
      if (progressTimerRef.current != null) {
        window.clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  const selectMeal = (meal: MealTime) => {
    setMealTime(meal);
    setStep('input');
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /** Inicia barra de progreso "fake" (la edge function no nos da
   *  progreso real). Sube de 0 → 90% en ~5s con desaceleración, y se
   *  queda esperando. Cuando llega la respuesta, llamamos a
   *  `finishProgress` para llevarla a 100%. */
  const startFakeProgress = () => {
    setAnalyzeProgress(0);
    const startedAt = Date.now();
    const targetMs = 5000; // 5s para llegar a 90%
    if (progressTimerRef.current != null) {
      window.clearInterval(progressTimerRef.current);
    }
    progressTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      // Curva ease-out: rápida al inicio, lenta hacia 90%
      const t = Math.min(1, elapsed / targetMs);
      const eased = 1 - Math.pow(1 - t, 2);
      const pct = Math.min(90, eased * 90);
      setAnalyzeProgress(pct);
      if (pct >= 90) {
        if (progressTimerRef.current != null) {
          window.clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
      }
    }, 60);
  };

  const finishProgress = (then: () => void) => {
    if (progressTimerRef.current != null) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setAnalyzeProgress(100);
    // Pequeño delay para que el usuario vea el 100% antes de pasar
    setTimeout(then, 300);
  };

  const analyzeWithAI = async (text?: string, imageBase64?: string, imageType?: string) => {
    if (!user) { sonnerToast.error('Inicia sesión primero'); return; }
    if (!text?.trim() && !imageBase64) {
      sonnerToast.error('Escribe qué comiste o sube una foto');
      return;
    }
    lastAttemptRef.current = { text, imageBase64, imageType };
    setStep('analyzing');
    setErrorMsg('');
    startFakeProgress();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sesión expirada, vuelve a iniciar sesión');
      }
      const res = await fetch(
        getFunctionUrlWithApiKey('analyze-food'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ text: text || undefined, imageBase64, imageType }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Error al analizar');
      if (!data?.success || !data?.items?.length) {
        throw new Error(data?.error || 'No se detectaron alimentos en lo que enviaste');
      }

      const items: AnalyzedItem[] = data.items.map((it: any) => ({
        item: it.item,
        calories: Number(it.calories) || 0,
        protein: Number(it.protein) || 0,
        carbs: Number(it.carbs) || 0,
        fat: Number(it.fat) || 0,
      }));
      finishProgress(() => {
        setAnalyzedItems(items);
        setStep('preview');
      });
    } catch (e: any) {
      finishProgress(() => {
        setErrorMsg(e?.message || 'Error inesperado al analizar con IA');
        setStep('error');
      });
    }
  };

  /** Persistir los items analizados a la BD y cerrar. */
  const confirmAndSave = async () => {
    if (!user || analyzedItems.length === 0) return;
    const today = getTodayLocalString();
    const inserts = analyzedItems.map(it => ({
      user_id: user.id,
      profile_name: activeUser,
      item_name: it.item,
      calories: it.calories,
      protein: it.protein,
      carbs: it.carbs,
      fat: it.fat,
      meal_type: mealTime,
      log_date: today,
    }));
    const savedItems: SavedFoodItem[] = inserts.map(ins => ({
      item_name: ins.item_name,
      calories: ins.calories,
      protein: ins.protein,
      carbs: ins.carbs,
      fat: ins.fat,
      meal_type: ins.meal_type,
      log_date: ins.log_date,
    }));

    const totalCals = inserts.reduce((s, i) => s + i.calories, 0);
    const mealLabel = MEAL_OPTIONS.find(m => m.value === mealTime)?.label || mealTime;
    const itemsLabel = inserts.length === 1 ? 'alimento registrado' : `${inserts.length} alimentos registrados`;
    sonnerToast.success(`${itemsLabel} en ${mealLabel}: ~${totalCals} kcal`);

    onSaved(savedItems);
    reset();
    onClose();

    // Persistir en background (offline-aware). El optimistic update
    // ya se hizo en el padre.
    if (!navigator.onLine) {
      for (const ins of inserts) {
        await offlineMutation('food_logs', 'insert', ins, () =>
          supabase.from('food_logs').insert(ins)
        );
      }
    } else {
      await offlineMutation('food_logs', 'insert', inserts[0], () =>
        supabase.from('food_logs').insert(inserts)
      );
    }
  };

  const discardPreview = () => {
    setAnalyzedItems([]);
    setStep('input');
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      sonnerToast.error('Usa JPG, PNG o WEBP');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      sonnerToast.error('Máx 10MB');
      return;
    }
    const base64 = await fileToBase64(file);
    await analyzeWithAI(aiInput || undefined, base64, file.type);
  };

  if (!open) return null;

  const selectedMeal = MEAL_OPTIONS.find(m => m.value === mealTime);
  const totalKcalPreview = analyzedItems.reduce((s, i) => s + i.calories, 0);

  // Header dinámico según el paso
  const headerTitle = (() => {
    switch (step) {
      case 'meal':      return '¿Qué comida es?';
      case 'input':     return `Registrar ${selectedMeal?.emoji} ${selectedMeal?.label}`;
      case 'analyzing': return 'Analizando con IA…';
      case 'preview':   return '¿Es lo que comiste?';
      case 'error':     return 'No pudimos analizar';
    }
  })();

  // Solo permitir cerrar si NO está analizando — durante el análisis
  // mantener cerrado el botón cerrar para evitar race conditions.
  const closeDisabled = step === 'analyzing';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-card border-t-4 sm:border-4 border-border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-border">
          <div className="flex items-center gap-2">
            {step === 'input' && (
              <button onClick={() => setStep('meal')} className="text-muted-foreground hover:text-foreground p-1">
                <ChevronLeft size={20} />
              </button>
            )}
            <div>
              <h3 className="text-base font-black uppercase italic">{headerTitle}</h3>
              {step === 'input' && (
                <p className="text-xs text-muted-foreground">Describe lo que comiste y la IA calculará las calorías</p>
              )}
            </div>
          </div>
          {!closeDisabled && (
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground p-1">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="p-4 space-y-3">

          {/* STEP 1: MEAL SELECTOR */}
          {step === 'meal' && (
            <div className="grid grid-cols-2 gap-3">
              {MEAL_OPTIONS.map(m => (
                <button
                  key={m.value}
                  onClick={() => selectMeal(m.value)}
                  className="flex items-center gap-3 p-4 rounded-xl border-2 border-border bg-secondary hover:border-accent hover:bg-accent/10 transition-all text-left group"
                >
                  <span className="text-2xl group-hover:scale-110 transition-transform">{m.emoji}</span>
                  <span className="font-black text-sm uppercase">{m.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: AI INPUT */}
          {step === 'input' && (
            <>
              <textarea
                rows={3}
                placeholder={`Ej: 2 huevos revueltos con espinaca y 1 tortilla de maíz`}
                className="fitness-input w-full resize-none"
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey && aiInput.trim()) {
                    e.preventDefault();
                    analyzeWithAI(aiInput);
                  }
                }}
                autoFocus
              />

              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoUpload}
              />

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="fitness-btn-secondary flex items-center justify-center gap-2 py-3"
                >
                  <Camera size={16} />
                  <span className="font-bold text-sm">Foto</span>
                </button>

                <button
                  onClick={() => analyzeWithAI(aiInput)}
                  disabled={!aiInput.trim()}
                  className="fitness-btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-60"
                >
                  <Zap size={16} />
                  <span className="font-bold text-sm">Calcular calorías</span>
                </button>
              </div>

              <p className="text-[10px] text-muted-foreground text-center">
                Enter para calcular · Shift+Enter para nueva línea
              </p>
            </>
          )}

          {/* STEP 3: ANALYZING — pantalla visual con progreso */}
          {step === 'analyzing' && (
            <div className="py-6 flex flex-col items-center text-center">
              {/* Ícono animado: gradient circular con sparkle pulsando */}
              <div className="relative mb-5">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-accent/20 via-amber-500/20 to-orange-500/20 flex items-center justify-center animate-pulse-soft">
                  <Sparkles className="w-12 h-12 text-accent animate-pulse" />
                </div>
                {/* Anillo decorativo girando */}
                <div className="absolute inset-0 rounded-full border-4 border-accent/30 border-t-accent animate-spin" style={{ animationDuration: '1.5s' }} />
              </div>

              <h4 className="text-base font-black uppercase tracking-wide mb-1">
                Analizando con IA
              </h4>
              <p className="text-xs text-muted-foreground mb-5 max-w-xs">
                Estamos calculando las calorías y macros de lo que comiste. Tarda unos segundos…
              </p>

              {/* Barra de progreso */}
              <div className="w-full max-w-xs">
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-accent via-amber-500 to-green-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${analyzeProgress}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                    Procesando
                  </span>
                  <span className="text-[10px] font-bold text-accent">
                    {Math.round(analyzeProgress)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: PREVIEW — confirmación de items detectados */}
          {step === 'preview' && (
            <div>
              <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-green-500/10 border-2 border-green-500/30">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-green-700 dark:text-green-300">
                    Análisis completado
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Detectamos {analyzedItems.length === 1 ? '1 alimento' : `${analyzedItems.length} alimentos`} · {totalKcalPreview} kcal totales
                  </p>
                </div>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto">
                {analyzedItems.map((it, i) => (
                  <div key={i} className="p-3 rounded-lg border-2 border-border bg-secondary/50">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="font-bold text-sm flex-1 min-w-0 truncate">{it.item}</span>
                      <span className="font-black text-accent text-sm shrink-0">{it.calories} kcal</span>
                    </div>
                    <div className="flex gap-3 text-[10px] text-muted-foreground">
                      <span>P: <span className="font-bold text-foreground">{Math.round(it.protein)}g</span></span>
                      <span>C: <span className="font-bold text-foreground">{Math.round(it.carbs)}g</span></span>
                      <span>G: <span className="font-bold text-foreground">{Math.round(it.fat)}g</span></span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  onClick={discardPreview}
                  className="fitness-btn-secondary py-3 font-bold text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmAndSave}
                  className="fitness-btn-primary py-3 font-bold text-sm"
                >
                  Aceptar y guardar
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Si la IA se equivocó en algo, dale Cancelar y vuelve a describir mejor.
              </p>
            </div>
          )}

          {/* STEP 5: ERROR — UI estándar con opción de crear ticket */}
          {step === 'error' && (
            <div>
              <ErrorWithTicket
                title="No pudimos analizar tu alimento"
                message={errorMsg || 'Error inesperado'}
                onRetry={() => {
                  const a = lastAttemptRef.current;
                  void analyzeWithAI(a.text, a.imageBase64, a.imageType);
                }}
                ticketContext={{
                  section: 'food-ai-analyze',
                  extra: {
                    meal: MEAL_OPTIONS.find(m => m.value === mealTime)?.label || mealTime,
                    text: lastAttemptRef.current.text,
                    hasImage: !!lastAttemptRef.current.imageBase64,
                    imageType: lastAttemptRef.current.imageType,
                    errorMessage: errorMsg,
                  },
                }}
              />

              <button
                onClick={() => setStep('input')}
                className="mt-3 w-full fitness-btn-secondary py-3 font-bold text-sm"
              >
                Volver atrás
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
